import React from "react";

import { Box, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from "@mui/material";
import { formatNumber, formatPercentage, formatPrice } from "utility/formatters";

const KPICalculationTable = ({ calculation, fullWidth = false }) => {
  if (!calculation) return null;

  const formatValue = (value, inputName) => {
    // Check if this is a ratio/percentage value based on the calculation name or input name
    const isPercentageCalc =
      calculation.name?.toLowerCase().includes("ratio") || calculation.name?.toLowerCase().includes("return");
    const isPercentageInput = inputName?.toLowerCase().includes("ratio");

    // For ratio calculations, only the final result should be shown as percentage
    if (isPercentageCalc && isPercentageInput) {
      return formatPercentage(value, 2);
    }

    // Format as price if it's a large monetary value
    if (Math.abs(value) > 100) {
      return formatPrice(value, 0);
    }
    // Otherwise format as a number with appropriate decimal places
    return formatNumber(value, value % 1 === 0 ? 0 : 2);
  };

  return (
    <Box
      sx={{
        mt: 1,
        mb: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: fullWidth ? "stretch" : "flex-start",
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: fullWidth ? "100%" : "fit-content",
          minWidth: 140,
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: fullWidth ? "stretch" : "flex-start",
        }}
      >
        <TableContainer
          className="kpi-calculation-table"
          sx={{
            width: "100%",
            boxShadow: 0,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.200",
            backgroundColor: "grey.50",
            overflowX: "auto",
            py: 1,
            px: 1.5,
          }}
        >
          <Table
            size="small"
            sx={{
              width: "100%",
              "& .MuiTableCell-root": {
                padding: "2px 6px",
                fontSize: "0.72rem",
                borderBottom: "1px solid rgba(224, 224, 224, 0.2)",
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableBody>
              {calculation.inputs.map((input, index) => (
                <TableRow key={index}>
                  <TableCell
                    sx={{
                      color: input.isSubItem ? "text.secondary" : "text.primary",
                      pl: input.isSubItem ? 2 : 1,
                      fontFamily: input.isSubItem ? "monospace" : "inherit",
                      fontSize: input.isSubItem ? "0.68rem" : "0.72rem",
                      border: 0,
                    }}
                  >
                    {input.name}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: input.isSubItem ? "normal" : "medium",
                      color: input.value < 0 ? "error.main" : "text.primary",
                      border: 0,
                    }}
                  >
                    {input.value && formatValue(input.value, input.name)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {calculation.explanations && (
          <Box
            sx={{
              width: "100%",
              mt: 1,
              px: 1.5,
              py: 1,
              backgroundColor: "rgba(25, 118, 210, 0.08)",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "rgba(25, 118, 210, 0.3)",
              wordWrap: "break-word",
              whiteSpace: "normal",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontWeight: "bold",
                fontSize: "0.75rem",
                color: "text.primary",
                mb: 0.5,
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              {calculation.explanations.primary}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: "0.72rem",
                color: "text.secondary",
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              {calculation.explanations.secondary}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            width: "100%",
            mt: 1,
            px: 1.5,
            py: 1,
            backgroundColor: "grey.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.100",
            fontSize: "0.68rem",
            color: "text.secondary",
            textAlign: fullWidth ? "left" : "center",
            fontFamily: "monospace",
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >
          {calculation.formula}
        </Box>
      </Box>
    </Box>
  );
};

export default KPICalculationTable;
